import { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import '../scss/salary-form.style.scss';
import '../scss/salary-calculator-violet.style.scss';
import PillTabs, { PillTab } from "./dashboard/pill-tabs.component";
import { useTranslation } from "react-i18next";
import FormField from './form-field';
import SalaryResultCard from './salary-result-card.component';
import CurrencyInput from './currency-input.component';
import { notify } from '../helper/notify';
import { toast } from 'react-toastify';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Tooltip } from './../components/images/svg/icons';
import { getSocialFeeByAge, handleToggle, defaultWorkingHour, CURRENCIES, AGE_RANGES, TAB_CONFIG, VAT_OPTIONS, tabs } from '../helper/salary.helpers';
import GenericTooltip from "./generictooltip";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { clearSalaryData } from "../features/salarycalculator/salary.slice";
import { FormState } from '../types/salary-calculator.types';
import InvoiceAdvancedCalculator from "./invoice-advanced-calculator.component";

/* Inline lucide-style icons (MIT) for the violet pill tabs */
const TAB_ICONS: Record<'invoice' | 'gross' | 'net', JSX.Element> = {
    invoice: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
        </svg>
    ),
    gross: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
        </svg>
    ),
    net: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
            <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
        </svg>
    ),
};

export const SalaryCalculator = ({ isDashboardPage = true }) => {
    const [activeTab, setActiveTab] = useState<keyof typeof TAB_CONFIG>('invoice');
    const [selectedVat, setSelectedVat] = useState('25');
    const [selectedTax, setSelectedTax] = useState<string>('30');
    const [selectedAgeRange, setSelectedAgeRange] = useState('0-66 years');
    const [currency, setCurrency] = useState('SEK');
    const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [selectedPension, setSelectedPension] = useState<string | null>('Select pension');
    const [selectedHolidayPay, setSelectedHolidayPay] = useState<string | null>('Select holiday pay');
    const [formData, setFormData] = useState<FormState>(() => ({
        amount: 0,
        GrossAmount: 0,
        NetAmount: 0,
        tax: '30',
        socialFee: getSocialFeeByAge('0-66 years'),
        currencyName: 'SEK',
        vat: 25,
        fee: 1.4,
        workingHours: [],
    }));
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 576px)');
    const dispatch = useAppDispatch();
    const { invoiceData, grossData: grossResultData, netData: netResultData, loading, } = useAppSelector((state) => state?.salary);
    const currentConfig = useMemo(() => TAB_CONFIG[activeTab], [activeTab]);
    const currentAmount = useMemo(() => formData[currentConfig.amountField], [formData, currentConfig.amountField]);
    const [mileageAmount, setMileageAmount] = useState<number>();
    const [isMileageTaxFree, setIsMileageTaxFree] = useState<boolean>(true);
    const [showMileageOnInvoice, setShowMileageOnInvoice] = useState<boolean>(true);
    const [travelExpenseAmount, setTravelExpenseAmount] = useState<number>();
    const [travelVat, setTravelVat] = useState<string>('0');
    const [showTravelExpensesOnInvoice, setShowTravelExpensesOnInvoice] = useState<boolean>(true);
    const [materialsAndToolsAmount, setMaterialsAndToolsAmount] = useState<number>();
    const [materialsToolsVat, setMaterialsToolsVat] = useState<string>('0');
    const [showMaterialsAndToolsOnInvoice, setShowMaterialsAndToolsOnInvoice] = useState<boolean>(true);
    const [swedenAllowance, setSwedenAllowance] = useState<number>();
    const [isSwedenAllowanceTaxFree, setIsSwedenAllowanceTaxFree] = useState<boolean>(true);
    const [allowanceOutsideSweden, setAllowanceOutsideSweden] = useState<number>();
    const [isAllowanceOutsideSwedenTaxFree, setIsAllowanceOutsideSwedenTaxFree] = useState<boolean>(true);
    const [isRotDeductionApplied, setIsRotDeductionApplied] = useState<boolean>(false);
    const [isRutDeductionApplied, setIsRutDeductionApplied] = useState<boolean>(false);
    const [isPensionSelected, setIsPensionSelected] = useState<boolean>(false);
    const [isHolidayPaySelected, setIsHolidayPaySelected] = useState<boolean>(false);
    type CustomExpenseType = 'mileage' | 'travel' | 'materials' | 'allowance_sweden' | 'allowance_outside_sweden';
    interface CustomExpense {
        id: number;
        type: CustomExpenseType;
        amount: number;
        articleNumber?: string;
        workingDescription?: string;
        vat?: number;
        isTaxFreeArticle?: boolean;
        isArticleShownInInvoice?: boolean;
    }
    const [customExpenses, setCustomExpenses] = useState<CustomExpense[]>([]);
    const [isMaterialsToolsTaxFree, setIsMaterialsToolsTaxFree] = useState<boolean>(true);
    const [showForeignAllowance, setShowForeignAllowance] = useState<boolean>(true);
    const [showSwedenAllowance, setShowSwedenAllowance] = useState<boolean>(true);

    const handleAddCustomExpense = useCallback((type: CustomExpenseType) => {
        let defaultExpense: CustomExpense = {
            id: Date.now(),
            type,
            amount: 0,
            vat: 25,
            isTaxFreeArticle: true,
            isArticleShownInInvoice: true,
        };
        switch (type) {
            case 'mileage':
                defaultExpense.articleNumber = '87';
                defaultExpense.workingDescription = 'Mileage Report';
                break;
            case 'travel':
                defaultExpense.articleNumber = '91';
                defaultExpense.workingDescription = 'Travel Expenses as per attached PDF';
                break;
            case 'materials':
                defaultExpense.articleNumber = '2';
                defaultExpense.workingDescription = 'Expenses for material and tools as per attached pdf';
                break;
            case 'allowance_sweden':
                defaultExpense.articleNumber = '83';
                defaultExpense.workingDescription = 'Allowance in Sweden';
                break;
            case 'allowance_outside_sweden':
                defaultExpense.articleNumber = '85';
                defaultExpense.workingDescription = 'Allowance outside Sweden';
                break;
        }
        setCustomExpenses(prev => [...prev, defaultExpense]);
    }, []);

    const handleCustomExpenseChange = useCallback((id: number, field: string, value: any) => {
        setCustomExpenses(prev => prev.map(exp => exp.id === id ? { ...exp, [field]: value } : exp));
    }, []);

    const handleRemoveCustomExpense = useCallback((id: number) => {
        setCustomExpenses(prev => prev.filter(exp => exp.id !== id));
    }, []);


    const resultData = useMemo(() => (invoiceData || grossResultData || netResultData) as Record<string, any>, [invoiceData, grossResultData, netResultData]);
    const updateForm = useCallback((fields: Partial<FormState>) => setFormData(prev => ({ ...prev, ...fields })), []);

    useEffect(() => {
        dispatch(clearSalaryData());
        updateForm({
            socialFee: getSocialFeeByAge(selectedAgeRange),
            currencyName: currency,
            vat: parseFloat(selectedVat) || 0,
        });
    }, [activeTab, selectedAgeRange, currency, selectedVat, dispatch, updateForm]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        updateForm({ [name]: type === 'checkbox' ? checked : parseFloat(value) });
    }, [updateForm]);

    const handleCurrencyChange = useCallback((cur: string) => {
        setCurrency(cur);
        updateForm({ currencyName: cur });
    }, [updateForm]);

    const handleVatChange = useCallback((vat: string | null) => {
        if (!vat) return;
        const parsedVat = parseFloat(vat) || 0;
        setSelectedVat(vat);
        updateForm({
            vat: parsedVat,
            workingHours: formData.workingHours?.map(item => {
                // Preserve VAT for deductions that have their own VAT input fields
                // Article 91: Travel Expenses, Articles 1/2: Materials & Tools (old V1 article numbers)
                // Article 98: Travel Expenses PDF, Article 99: Materials/Tools PDF (new V2 article numbers)
                const shouldPreserveVat = item.articleNumber && ['1', '2', '91', '98', '99'].includes(item.articleNumber);
                return { ...item, vat: shouldPreserveVat ? item.vat : parsedVat };
            }) || [],
        });
    }, [formData.workingHours, updateForm]);

    useEffect(() => {
        setSelectedTax(formData.tax);
    }, [formData.tax]);

    const handleTaxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            if (value === '') {
                setSelectedTax('');
                updateForm({ tax: '' });
                return;
            }

            if (!/^\d*(?:[.,]?\d*)?$/.test(value)) return;

            const num = Number(value);

            if (isNaN(num)) {
                setSelectedTax(value);
                updateForm({ tax: value });
                return;
            }

            const finalValue = num > 55 ? '30' : value;

            setSelectedTax(finalValue);
            updateForm({ tax: finalValue });
        },
        [updateForm]
    );

    const handleAgeRangeChange = useCallback((range: string) => {
        setSelectedAgeRange(range);
        updateForm({ socialFee: getSocialFeeByAge(range) });
    }, [updateForm]);

    const handleCalculate = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        const amount = currentAmount;
        if (!amount || amount <= 0) {
            notify.error(t('PLEASE_ENTER_VALID_AMOUNT') as string, t('TOAST_ENTER_AMOUNT_SUB') as string);
            return;
        }
        try {
            const baseCalculationData = {
                ...formData,
                amount: 0,
                vat: parseFloat(selectedVat) || 25,
                currencyName: currency,
                socialFee: getSocialFeeByAge(selectedAgeRange),
            };
            const totalCalculatedAmount = (currentAmount + (showMileageOnInvoice ? Number(mileageAmount) || 0 : 0) + (showTravelExpensesOnInvoice ? travelExpenseAmount || 0 : 0) + (showMaterialsAndToolsOnInvoice ? materialsAndToolsAmount || 0 : 0) + (showSwedenAllowance ? swedenAllowance || 0 : 0) + (showForeignAllowance ? allowanceOutsideSweden || 0 : 0) + customExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0));
            const calculationData = {
                ...(baseCalculationData && {
                    amount: parseInt(totalCalculatedAmount || amount),
                    rootDeduction: isRotDeductionApplied,
                    rutDeduction: isRutDeductionApplied,
                    tax: (selectedTax),
                    socialFee: getSocialFeeByAge(selectedAgeRange),
                    vat: parseFloat(selectedVat) || 25,
                    currencyName: currency,
                    createdDate: new Date().toISOString(),
                }),
                ...(isAdvancedOpen && {
                    ...(isPensionSelected && {
                        pension: isPensionSelected,
                        pensionAmount: isPensionSelected ? selectedPension : 1000
                    }),
                    ...(isHolidayPaySelected && {
                        holidaySavings: isHolidayPaySelected,
                        holidaySavingsFee: selectedHolidayPay ? selectedHolidayPay : 12
                    }),
                    rootDeduction: isRotDeductionApplied,
                    rutDeduction: isRutDeductionApplied,
                    tax: Number(selectedTax),
                    socialFee: getSocialFeeByAge(selectedAgeRange),
                    vat: parseFloat(selectedVat) || 0,
                    expenditureForMaterialsAndToolsTaxFreeAmount: isMaterialsToolsTaxFree ? materialsAndToolsAmount : 0,
                    workingHours: [
                        {
                            amount: amount,
                            vat: parseFloat(selectedVat) || 0,
                            currencyName: currency,
                            articleNumber: "97",
                            isArticleShownInInvoice: true
                        },
                        {
                            ...(formData.workingHours[0] || {}),
                            amount: mileageAmount || 0,
                            vat: parseFloat(selectedVat) || 0,
                            articleNumber: isMileageTaxFree ? '87' : '88',
                            workingDescription: 'Mileage Report',
                            isTaxFreeArticle: isMileageTaxFree,
                            isArticleShownInInvoice: showMileageOnInvoice
                        },
                        {
                            ...(formData.workingHours[0] || {}),
                            amount: travelExpenseAmount || 0,
                            articleNumber: '91',
                            workingDescription: 'Travel Expenses as per attached PDF',
                            vat: travelVat && travelVat !== '' && travelVat !== 'Select VAT' ? travelVat : 0,
                            // isTaxFreeArticle: true,
                            isArticleShownInInvoice: showTravelExpensesOnInvoice
                        },
                        {
                            ...(formData.workingHours[0] || {}),
                            amount: materialsAndToolsAmount || 0,
                            articleNumber: isMaterialsToolsTaxFree ? '2' : '1',
                            workingDescription: 'Expenses for material and tools as per attached pdf',
                            vat: materialsToolsVat && materialsToolsVat !== '' && materialsToolsVat !== '0' && materialsToolsVat !== 'Select VAT' ? materialsToolsVat : 0,
                            isTaxFreeArticle: isMaterialsToolsTaxFree,
                            isArticleShownInInvoice: isMaterialsToolsTaxFree && showMaterialsAndToolsOnInvoice
                        },
                        {
                            ...(formData.workingHours[0] || {}),
                            amount: swedenAllowance || 0,
                            articleNumber: isSwedenAllowanceTaxFree ? '83' : '84',
                            workingDescription: 'Allowance in Sweden',
                            vat: parseFloat(selectedVat) || 0,
                            isTaxFreeArticle: isSwedenAllowanceTaxFree,
                            isArticleShownInInvoice: showSwedenAllowance
                        },
                        {
                            ...(formData.workingHours[0] || {}),
                            amount: allowanceOutsideSweden || 0,
                            articleNumber: isAllowanceOutsideSwedenTaxFree ? '85' : '86',
                            vat: parseFloat(selectedVat) || 0,
                            workingDescription: 'Allowance outside Sweden',
                            isTaxFreeArticle: isAllowanceOutsideSwedenTaxFree,
                            isArticleShownInInvoice: showForeignAllowance
                        },
                        ...customExpenses.map(exp => ({
                            amount: exp.amount || 0,
                            articleNumber: exp.articleNumber,
                            workingDescription: exp.workingDescription,
                            vat: exp.vat,
                            isTaxFreeArticle: exp.isTaxFreeArticle,
                            isArticleShownInInvoice: exp.isArticleShownInInvoice,
                        })),
                    ],
                }),
            };
            if (activeTab === 'invoice') {
                await dispatch(currentConfig.action(calculationData));
            } else {
                await dispatch(currentConfig.action(baseCalculationData));
            }
            notify.success(t('CALCULATION_SUCCESS') as string, t('TOAST_CALC_SUCCESS_SUB') as string);
        } catch (error) {
            notify.error(t('FAILED_TO_CALCULATE') as string, t('TOAST_TRY_AGAIN_SUB') as string);
        }
    }, [currentAmount, formData, travelVat, selectedVat, currency, selectedAgeRange, isAdvancedOpen, dispatch, isMaterialsToolsTaxFree, showMaterialsAndToolsOnInvoice, currentConfig.action, t, activeTab, selectedPension, selectedHolidayPay, isRotDeductionApplied, isRutDeductionApplied, isMileageTaxFree, showTravelExpensesOnInvoice, showMileageOnInvoice, mileageAmount, travelExpenseAmount, materialsAndToolsAmount, materialsToolsVat, swedenAllowance, isSwedenAllowanceTaxFree, allowanceOutsideSweden, isAllowanceOutsideSwedenTaxFree, showForeignAllowance, showSwedenAllowance, customExpenses]);

    const handleClean = useCallback(() => {
        updateForm({
            amount: 0,
            GrossAmount: 0,
            NetAmount: 0,
            tax: selectedTax,
            socialFee: getSocialFeeByAge('0-66 years'),
            currencyName: 'SEK',
            vat: 25,
            workingHours: isAdvancedOpen ? [defaultWorkingHour()] : [],
        });
        setSelectedVat('25');
        setSelectedTax('30');
        setSelectedAgeRange('0-66 years');
        setCurrency('SEK');
        dispatch(clearSalaryData());
        toast.success(t('VALUE_SET_TO_DEFAULT') as string);
    }, [isAdvancedOpen, dispatch, updateForm, t]);
    const handleMileageAmount = useCallback((val: number, index = 0) => {
        setMileageAmount(val);

        const updatedWorkingHours = formData.workingHours.map((item, idx) =>
            idx === index
                ? { ...item, amount: val, isTaxFreeArticle: isMileageTaxFree }
                : item
        );

        setFormData(prev => ({
            ...prev,
            workingHours: updatedWorkingHours,
        }));
    }, [isMileageTaxFree, setMileageAmount, setFormData, formData.workingHours]);

    function handleHolidaypay(value: string) {
        setSelectedHolidayPay(value)
        setIsHolidayPaySelected(!!value && value !== 'Select holiday pay');
        updateForm({
            holidaySavings: true,
            holidaySavingsFee: parseFloat(value) || 0,
            workingHours: formData.workingHours.map(item => ({ ...item, holidaySavingsFee: parseFloat(value) || 0 })),
        });
    }
    function handlePensionChange(value: string) {
        setSelectedPension(value);
        setIsPensionSelected(!!value && value !== 'Select pension');
        updateForm({
            pension: true,
            pensionAmount: parseFloat(value) || 0,
            workingHours: formData.workingHours.map(item => ({ ...item, holidaySavingsFee: parseFloat(value) || 0 })),
        });
    }

    const handleRotRutToggle = (val: boolean, type: 'ROT' | 'RUT') => {
        type === 'ROT'
            ? setIsRotDeductionApplied(val)
            : setIsRutDeductionApplied(val);
    };

    function handletabChange(key: keyof typeof TAB_CONFIG) {
        setActiveTab(key)
        updateForm({
            amount: 0,
            GrossAmount: 0,
            NetAmount: 0,
            tax: selectedTax,
            socialFee: getSocialFeeByAge('0-66 years'),
            currencyName: 'SEK',
            vat: 25,
            workingHours: isAdvancedOpen ? [defaultWorkingHour()] : [],
        });
    }

    const violet = !isDashboardPage;
    const pillTabItems: PillTab[] = tabs.map(({ key, label }) => ({
        key,
        label: t(label),
        icon: TAB_ICONS[key as keyof typeof TAB_ICONS],
    }));

    return (
        <div className={`salary-calculator-wrapper px-3 px-sm-0 ${violet ? 'violet-salary' : ''}`}>
            <Container className={`salary-calculator-container w-sm-100 ${!isDashboardPage || isMobile ? 'px-0' : ''}`}>
                {isDashboardPage && (<>
                    <h2 className="Heading_h2 text-start text-sm-center mb-3 ">{t('SALARY_CALCULATOR')}</h2>
                    <h6 className="Heading_h6 text-sm-center text-start w-68 mx-auto   margin-80 mb-5 mt-3 mt-sm-0 mb-sm-0">
                        {t('SALARY_CALCULATOR_DESCRIPTION')}
                    </h6>
                </>)}
                <Row className="salary-calculator-row ">
                    <Col md={violet ? 7 : 5} xs={12} className={`px-0 salary-form-col mb-4 ${violet ? 'vs-reveal' : ''}`} style={violet ? { animationDelay: '140ms' } : undefined}>
                        <form className="salary-form" onSubmit={handleCalculate}>
                            {violet ? (
                                <div className="mb-4">
                                    <PillTabs
                                        tabs={pillTabItems}
                                        activeTab={activeTab}
                                        onTabChange={(key) => handletabChange(key as keyof typeof TAB_CONFIG)}
                                        ariaLabel={t('SALARY_CALCULATOR')}
                                    />
                                </div>
                            ) : (
                                <div className="tabs flex-row">
                                    {tabs.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            className={`tab ${activeTab === key ? 'active' : ''}`}
                                            type="button"
                                            onClick={() => handletabChange(key as keyof typeof TAB_CONFIG)}
                                        >
                                            {t(label)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="form-group">
                                <div>
                                    <label className="form-label">
                                        {t(currentConfig.label)}
                                    </label>
                                    <span className="vs-tip-anchor">
                                        <img src={Tooltip} className="tooltip-svg cursor-pointer" onClick={() => handleToggle(setActiveTooltipId, 'amount')} onMouseEnter={() => setActiveTooltipId('amount')} onMouseLeave={() => setActiveTooltipId(null)} alt="" />
                                        <GenericTooltip
                                            isActive={activeTooltipId === 'amount'}
                                            content={activeTab.includes('invoice') ? t('INVOICE_AMOUNT_EXCLUDING_VAT') : activeTab.includes('gross') ? t('GROSS SALARY BEFORE TAX AND DEDUCTIONS') : t('NET_AMOUNT_EXCLUDING_VAT')}
                                            onClose={() => setActiveTooltipId(null)}
                                            width="w-50"
                                            position="left-0"
                                            zIndex="z-index-9"
                                        />
                                    </span>
                                </div>
                                <CurrencyInput
                                    value={currentAmount}
                                    onChange={handleChange}
                                    currency={currency}
                                    onCurrencyChange={handleCurrencyChange}
                                    currencies={CURRENCIES}
                                    placeholder={'0'}
                                    name={currentConfig.amountField}
                                />
                            </div>

                            <FormField
                                type="select"
                                label={t('YOUR_AGE')}
                                name="ageRange"
                                value={selectedAgeRange}
                                onChange={e => handleAgeRangeChange(e.target.value)}
                                activeTooltipId={activeTooltipId}
                                handleTogle={handleToggle}
                                setActiveTooltipId={setActiveTooltipId}
                                options={AGE_RANGES}
                                isNote={false}
                                tooltipLabel="USER_AGE_FOR_SOCIAL_SECURITY_CONTRIBUTIONS"
                            />
                            {(!activeTab.includes('invoice')) && (
                                <>
                                    <div className="my-5">
                                        <FormField
                                            type="select"
                                            label={t('VAT')}
                                            name="vat"
                                            value={selectedVat}
                                            currency={currency}
                                            activeTooltipId={activeTooltipId}
                                            setActiveTooltipId={setActiveTooltipId}
                                            onChange={e => handleVatChange(e.target.value)}
                                            options={VAT_OPTIONS.map(v => ({ label: `${v} %`, value: v }))}
                                            tooltipLabel="VALUE_ADDED_TAX_ON_INVOICE"
                                        />
                                    </div>
                                    <div className="my-5">
                                        <div className="form-group">
                                            <div>
                                                <label className="form-label">{t('TAX')}</label>
                                                <span className="vs-tip-anchor">
                                                    <img src={Tooltip} className="tooltip-svg cursor-pointer" onClick={() => handleToggle(setActiveTooltipId, 'tax')} onMouseEnter={() => setActiveTooltipId('tax')} onMouseLeave={() => setActiveTooltipId(null)} alt="" />
                                                    <GenericTooltip
                                                        isActive={activeTooltipId === 'tax'}
                                                        content={t('TAX_ON_INVOICE')}
                                                        onClose={() => setActiveTooltipId(null)}
                                                        width="w-60"
                                                        position="left-0"
                                                        zIndex="z-index-9"
                                                    />
                                                </span>
                                            </div>
                                            <div className="d-flex rounded align-items-center mt-2" style={{ border: '1px solid #EAEBF0', borderRadius: '6px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control border-0 shadow-none"
                                                    name="tax"
                                                    value={selectedTax}
                                                    onChange={handleTaxChange}
                                                    placeholder="30"
                                                    style={{ flex: 1 }}
                                                />
                                                <div className="vr" />
                                                <span className="px-3" style={{ color: 'rgba(24, 29, 39, 1)', opacity: 0.5 }}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )
                            }
                            <div className="advanced-calculator-section">
                                {(activeTab.includes('invoice')) && (
                                    <div
                                        className="advanced-toggle d-flex align-items-center justify-content-center"
                                        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={isAdvancedOpen}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsAdvancedOpen(!isAdvancedOpen); } }}
                                    >
                                        <span className="advanced-text">{t('ADVANCED_CALCULATOR')}</span>
                                        <span className="arrow-icon mx-2 mb-2">
                                            <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isAdvancedOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                <path d="M11 1.33331L6.58926 5.74406C6.26382 6.0695 5.73618 6.0695 5.41074 5.74406L1 1.33331" stroke="#181D27" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </span>
                                    </div>
                                )}

                                {isAdvancedOpen && (activeTab == 'invoice') && (
                                    <div className="vs-advanced-reveal">
                                    <InvoiceAdvancedCalculator
                                        currentAmount={currentAmount}
                                        tax={formData.tax}
                                        activeTooltipId={activeTooltipId}
                                        setActiveTooltipId={setActiveTooltipId}
                                        name="invoice-advanced-calculator"
                                        currency={currency}
                                        selectedVat={selectedVat}
                                        selectedTax={selectedTax}
                                        selectedPension={selectedPension}
                                        selectedHolidayPay={selectedHolidayPay}
                                        handleVatChange={handleVatChange}
                                        handleTaxChange={handleTaxChange}
                                        VAT_OPTIONS={VAT_OPTIONS}
                                        handleHolidaypay={handleHolidaypay}
                                        handlePensionChange={currentAmount >= 5000 ? handlePensionChange : () => { }}
                                        handleMileageAmount={handleMileageAmount}
                                        mileageAmount={mileageAmount}
                                        isRutDeductionApplied={isRutDeductionApplied}
                                        isRotDeductionApplied={isRotDeductionApplied}
                                        handleRotRutToggle={handleRotRutToggle}
                                        isMileageTaxFree={isMileageTaxFree}
                                        setIsMileageTaxFree={setIsMileageTaxFree}
                                        showMileageOnInvoice={showMileageOnInvoice}
                                        setShowMileageOnInvoice={setShowMileageOnInvoice}
                                        setTravelExpenseAmount={setTravelExpenseAmount}
                                        travelExpenseAmount={travelExpenseAmount as number}
                                        travelVat={travelVat}
                                        setTravelVat={setTravelVat}
                                        setMaterialsAndToolsAmount={setMaterialsAndToolsAmount}
                                        materialsAndToolsAmount={materialsAndToolsAmount as number}
                                        setMaterialsToolsVat={setMaterialsToolsVat}
                                        materialsToolsVat={materialsToolsVat}
                                        isMaterialsToolsTaxFree={isMaterialsToolsTaxFree}
                                        setIsMaterialsToolsTaxFree={setIsMaterialsToolsTaxFree}
                                        showMaterialsAndToolsOnInvoice={showMaterialsAndToolsOnInvoice}
                                        setShowMaterialsAndToolsOnInvoice={setShowMaterialsAndToolsOnInvoice}
                                        allowanceOutsideSweden={allowanceOutsideSweden as number}
                                        setAllowanceOutsideSweden={setAllowanceOutsideSweden}
                                        isSwedenAllowanceTaxFree={isSwedenAllowanceTaxFree}
                                        setIsSwedenAllowanceTaxFree={setIsSwedenAllowanceTaxFree}
                                        isAllowanceOutsideSwedenTaxFree={isAllowanceOutsideSwedenTaxFree}
                                        setIsAllowanceOutsideSwedenTaxFree={setIsAllowanceOutsideSwedenTaxFree}
                                        swedenAllowance={swedenAllowance as number}
                                        setSwedenAllowance={setSwedenAllowance}
                                        showSwedenAllowance={showSwedenAllowance}
                                        setShowSwedenAllowance={setShowSwedenAllowance}
                                        showForeignAllowance={showForeignAllowance}
                                        setShowForeignAllowance={setShowForeignAllowance}
                                        showTravelExpensesOnInvoice={showTravelExpensesOnInvoice}
                                        setShowTravelExpensesOnInvoice={setShowTravelExpensesOnInvoice}
                                        customExpenses={customExpenses}
                                        handleAddCustomExpense={handleAddCustomExpense}
                                        handleCustomExpenseChange={handleCustomExpenseChange}
                                        handleRemoveCustomExpense={handleRemoveCustomExpense}
                                    />
                                    </div>
                                )}
                            </div>
                            <button
                                className="calculate-btn"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        {t('CALCULATING')}....
                                        <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                                    </>
                                ) : (
                                    t('CALCULATE')
                                )}
                            </button>
                            <button className="clean-btn" type="button" onClick={handleClean} disabled={loading}>{t('CLEAN')}</button>
                        </form>
                    </Col>
                    <Col md={violet ? 5 : 7} xs={12} className={`px-0 ${violet ? 'salary-results-col vs-reveal' : ''}`} style={violet ? { animationDelay: '220ms' } : undefined}>
                        <SalaryResultCard
                            activeTab={activeTab}
                            data={resultData as any}
                            t={t}
                            violet={violet}
                            currencyName={currency}
                            selectedVat={selectedVat}
                            travelVat={travelVat}
                            customExpenses={customExpenses as any}
                            materialsToolsVat={materialsToolsVat}
                        />
                    </Col>
                </Row>
            </Container>
        </div>
    )
}
